import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import { Grid2, Paper, Typography } from "@mui/material";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	IRowNode,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "@/controls/buttons/FooterButton";
import { CenteredLazyLoading } from "@/controls/LazyLoading";

import { useNavigate } from "react-router-dom";
import { CatalogOperator } from "../../../models/CatalogOperator";
import { operatorUrl, useOperators } from "../CatalogOperatorPage";
import { useOperatorOptionsFunctions } from "../hooks/useOperatorOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { UpsertDeleteModal } from "./UpsertDeleteModal";

export const TableOperator = ({
	operatorId,
}: {
	operatorId: string | undefined;
}) => {
	const navigate = useNavigate();

	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [showModal, setShowModal] = useState<
		"create" | "update" | "delete" | ""
	>("");
	const [selectedRowData, setSelectedRowData] =
		useState<CatalogOperator | null>(null);
	const [error, setError] = useState("");

	const { data: rows, isLoading } = useOperators((error) =>
		setError(error.message),
	);

	const handleNewOperator = () => {
		if (gridApi) gridApi.deselectAll();
		setShowModal("create");
		setSelectedRowData(null);
		navigate(`${operatorUrl}new`);
	};

	const editOperator = (data: CatalogOperator) => {
		setSelectedRowData(data);
		setShowModal("update");
		navigate(`${operatorUrl}${data.OperatorId}`);
	};

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useOperatorOptionsFunctions({
		handleNewOperator,
		setSelectedRowData,
		setShowModal,
		editOperator,
	});
	const { columnDefs } = useTableData({
		setSelectedRowData,
		setShowModal,
		showContextMenu,
	});

	const { defaultColDef } = useGridDefinitions({});

	const onRowDoubleClicked = (event: any) => {
		setSelectedRowData(event.data as CatalogOperator);
		setShowModal("update");
		navigate(`${operatorUrl}${(event.data as CatalogOperator).OperatorId}`);
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "operatorContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[rows],
	);

	const onModalHide = (shouldUpdate: boolean) => {
		if (gridApi) gridApi.deselectAll();
		setShowModal("");
		navigate(operatorUrl);
	};

	useEffect(() => {
		if (!isLoading) {
			if (operatorId) {
				if (operatorId === "new") {
					handleNewOperator();
				} else {
					let operatorNode: IRowNode;

					gridApi?.forEachNode((n) => {
						if ((n.data as CatalogOperator).OperatorId === Number(operatorId)) {
							operatorNode = n;
							return true;
						}
					});

					if (operatorNode!) {
						gridApi?.ensureNodeVisible(operatorNode);
						operatorNode.setSelected(true);
						setSelectedRowData(operatorNode.data);
						setShowModal("update");
					}
				}
			}
		}
	}, [isLoading, gridApi]);

	useEffect(() => {
		registerConfig({
			id: "operatorContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<Grid2 container justifyContent="center" p={1} spacing={1}>
			<Grid2 size={{ md: 12, xs: 12 }}>
				<Typography variant="h5" fontWeight={600}>
					Operators
				</Typography>
			</Grid2>
			<Grid2
				container
				justifyContent="center"
				alignItems="center"
				style={{ width: "100%" }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Paper elevation={2} style={{ height: "64vh", width: "100%" }}>
						<AgGridReact
							loading={isLoading}
							rowData={rows || []}
							columnDefs={columnDefs}
							defaultColDef={defaultColDef}
							rowHeight={34}
							headerHeight={34}
							animateRows
							getContextMenuItems={getContextMenuItems}
							onRowDoubleClicked={onRowDoubleClicked}
							onGridReady={(params) => setGridApi(params.api)}
							getRowId={(params) =>
								String((params.data as CatalogOperator).OperatorId)
							}
							loadingOverlayComponent={CenteredLazyLoading}
							gridOptions={{
								rowSelection: {
									mode: "singleRow",
									checkboxes: false,
									enableClickSelection: true,
								},
							}}
						/>
					</Paper>
				</Grid2>
				<FooterButton
					label="OPERATOR"
					showCreateModal={showModal === "create"}
					setShowCreateModal={(showCreateModal) =>
						showCreateModal ? setShowModal("create") : setShowModal("")
					}
					sizes={{ md: 3 }}
				/>
			</Grid2>
			<UpsertDeleteModal
				operator={selectedRowData}
				onHide={onModalHide}
				modal={showModal}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Grid2>
	);
};
